import type {
  ModelConstructor,
  TargetableModel,
} from '@cotera/client/v0/actions/framework';
import type { WorkflowGraphApiData } from './workflow-graph.types';
import { isWorkflowTriggerFlowNodeId } from './workflow-graph-to-flow';
import { graphNodeToViewData } from './workflow-graph-view-data';
import {
  resolveGraphNodeRenderableNode,
  resolveTriggerRenderableNode,
  type WorkflowRenderCatalog,
} from './workflow-render-catalog';
import { WorkflowAgentNodeModel } from './workflow-node-models/workflow-agent-node.model';
import { WorkflowCronTriggerNodeModel } from './workflow-node-models/workflow-cron-trigger-node.model';
import { WorkflowEventTriggerNodeModel } from './workflow-node-models/workflow-event-trigger-node.model';
import { WorkflowDatasetTriggerNodeModel } from './workflow-node-models/workflow-dataset-trigger-node.model';
import { WorkflowWebhookTriggerFullNodeModel } from './workflow-node-models/workflow-webhook-trigger-full-node.model';
import { WorkflowWebhookTriggerPreviewNodeModel } from './workflow-node-models/workflow-webhook-trigger-preview-node.model';
import type { WorkflowModel } from './workflow.model';
import { workflowTriggerViewFromSnapshot } from './components/trigger/strategies';
import type { WorkflowEditorSelection } from './workflow-toolbar-action.types';

type WorkflowSelectionTargetActions = {
  targetModel: <T extends TargetableModel>(model: T) => void;
  removeTargetForType: <T extends TargetableModel>(
    model: ModelConstructor<T>
  ) => void;
  isInScope: <T extends TargetableModel>(
    modelConstructor: ModelConstructor<T>,
    id?: string
  ) => boolean;
  targetedModelOfType: <T extends TargetableModel>(
    type: ModelConstructor<T>
  ) => T | null;
  hasTargetedModelOfType: <T extends TargetableModel>(
    type: ModelConstructor<T>
  ) => boolean;
};

const NODE_VIEW_MODEL_TYPES: Array<ModelConstructor<TargetableModel>> = [
  WorkflowAgentNodeModel,
  WorkflowCronTriggerNodeModel,
  WorkflowEventTriggerNodeModel,
  WorkflowDatasetTriggerNodeModel,
  WorkflowWebhookTriggerFullNodeModel,
  WorkflowWebhookTriggerPreviewNodeModel,
];

function clearWorkflowNodeViewModelTargets(
  actions: WorkflowSelectionTargetActions
): void {
  for (const modelType of NODE_VIEW_MODEL_TYPES) {
    if (actions.hasTargetedModelOfType(modelType)) {
      actions.removeTargetForType(modelType);
    }
  }
}

function targetModelIfInScope(
  actions: WorkflowSelectionTargetActions,
  model: TargetableModel
): void {
  if (
    !actions.isInScope(
      model.constructor as ModelConstructor<TargetableModel>,
      model.id
    )
  ) {
    return;
  }

  const modelType = model.constructor as ModelConstructor<TargetableModel>;
  const currentTarget = actions.targetedModelOfType(modelType);
  if (currentTarget?.id === model.id && currentTarget.isTargeted === true) {
    return;
  }

  actions.targetModel(model);
}

/**
 * When canvas selection changes, mark the matching per-entity view model as
 * targeted so children can use {@link useTargetedModel} after ModelScope
 * registration on each flow node card.
 */
export function syncWorkflowSelectionTargetModel(params: {
  selection: WorkflowEditorSelection;
  graph: WorkflowGraphApiData | null;
  catalog: WorkflowRenderCatalog;
  workflowModel: WorkflowModel;
  actions: WorkflowSelectionTargetActions;
}): void {
  const { selection, graph, catalog, workflowModel, actions } = params;
  const { selectedNodeIds, selectedEdgeIds } = selection;

  if (
    graph === null ||
    selectedNodeIds.length !== 1 ||
    selectedEdgeIds.length > 0
  ) {
    clearWorkflowNodeViewModelTargets(actions);
    return;
  }

  const flowNodeId = selectedNodeIds[0];
  if (flowNodeId === undefined) {
    clearWorkflowNodeViewModelTargets(actions);
    return;
  }

  if (isWorkflowTriggerFlowNodeId(flowNodeId)) {
    const triggerId = flowNodeId.slice('trigger:'.length);
    const snapshot = graph.triggers.find((row) => row.trigger.id === triggerId);
    if (snapshot === undefined) {
      clearWorkflowNodeViewModelTargets(actions);
      return;
    }
    const triggerView = workflowTriggerViewFromSnapshot(snapshot);
    const entry = resolveTriggerRenderableNode(catalog, triggerView);
    if (entry.models === undefined) {
      clearWorkflowNodeViewModelTargets(actions);
      return;
    }
    const model = workflowModel.getRenderableNodeModel(entry.key, triggerId);
    clearWorkflowNodeViewModelTargets(actions);
    targetModelIfInScope(actions, model);
    return;
  }

  const graphNode = graph.nodes.find((node) => node.id === flowNodeId);
  if (graphNode === undefined) {
    clearWorkflowNodeViewModelTargets(actions);
    return;
  }

  const entry = resolveGraphNodeRenderableNode(catalog, graphNode.type);
  const resolveEntityId = entry.resolveModelEntityId;
  if (entry.models === undefined || resolveEntityId === undefined) {
    clearWorkflowNodeViewModelTargets(actions);
    return;
  }

  const entityId = resolveEntityId(graphNodeToViewData(graphNode));
  if (entityId === undefined || entityId === '') {
    clearWorkflowNodeViewModelTargets(actions);
    return;
  }

  const model = workflowModel.getRenderableNodeModel(entry.key, entityId);
  clearWorkflowNodeViewModelTargets(actions);
  targetModelIfInScope(actions, model);
}
